import React, { FC, useState } from 'react'
import { ContextNotification } from '@pandiafi/components'
import { Button, Heading, Paragraph } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'

import {
  ConfirmOperationType,
  ContractOperation,
} from '../../../../common/types'
import {
  PendingOperation,
  ResidentAppLocale,
} from '../../../../common/validations'

import { useAPIGetRegistrationEntryLink } from 'api/apiHooks/useAPIGetRegistrationEntryLink'
import { useAPIRejectPendingOperation } from 'api/apiHooks/useAPIRejectPendingOperation'
import { useAPIResumePendingOperation } from 'api/apiHooks/useAPIResumePendingOperation'
import { pendingOperationsParams } from 'api/apiHooks/useAPIGetPendingOperations'
import { queryClient } from 'reactQuery'

const resolveContractOperation = (
  operationType: PendingOperation['operationType']
) => {
  switch (operationType) {
    case ConfirmOperationType.RESIDENT_APP_CONFIRM_CONTRACT_MODIFICATION:
    case ConfirmOperationType.RESIDENT_APP_ACCEPT_CONTRACT_MODIFICATION:
    case ConfirmOperationType.RESIDENT_APP_REJECT_CONTRACT_MODIFICATION:
      return ContractOperation.MODIFICATION
    default:
      return ContractOperation.TERMINATION
  }
}

interface PendingOperationCardProps {
  operation: PendingOperation
  isImpersonator: boolean
  identificationRequired: boolean
}

export const PendingOperationCard: FC<PendingOperationCardProps> = ({
  operation,
  isImpersonator,
  identificationRequired,
}) => {
  const { t, i18n } = useTranslation(['frontPage'])

  const locale = i18n.language ?? ResidentAppLocale.FI

  const [rejectFailed, setRejectFailed] = useState(false)

  const { mutate: reject, isPending: isRejectPending } =
    useAPIRejectPendingOperation(operation.nonce, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: pendingOperationsParams.queryKey,
        })
      },
      onError: () => {
        setRejectFailed(true)
        queryClient.invalidateQueries({
          queryKey: pendingOperationsParams.queryKey,
        })
      },
    })

  const { mutate: resume, isPending: isResumePending } =
    useAPIResumePendingOperation(operation.contractUuid, locale, {
      onSuccess: (data) => {
        window.location.href = data.link
      },
    })

  const { mutate: getIdentificationLink, isPending: isIdentificationPending } =
    useAPIGetRegistrationEntryLink(locale, false)

  const isPending =
    isRejectPending || isResumePending || isIdentificationPending

  const contractOperation = resolveContractOperation(operation.operationType)

  const titleKey =
    contractOperation === ContractOperation.MODIFICATION
      ? 'frontPage:pendingOperations.modificationTitle'
      : 'frontPage:pendingOperations.terminationTitle'

  const handleContinue = () => {
    resume({ nonce: operation.nonce })
  }

  const handleIdentify = () => {
    getIdentificationLink(undefined, {
      onSuccess: (data) => {
        if (data.link) {
          window.location.assign(data.link)
        }
      },
    })
  }

  const handleReject = () => {
    setRejectFailed(false)
    reject()
  }

  return (
    <ContextNotification
      type="warning"
      rounded="m"
      icon="exclamation-triangle"
      className="w-full text-common-gray-8 min-tablet:min-w-80 min-tablet:max-w-md"
    >
      <Heading element="h4" semibold size="m" className="mb-1">
        {t(titleKey)}
      </Heading>
      <Paragraph>
        {identificationRequired
          ? t('frontPage:pendingOperations.identificationRequired')
          : t('frontPage:pendingOperations.description')}
      </Paragraph>
      <div className="mt-4 flex flex-col gap-2 min-tablet:flex-row min-tablet:flex-wrap">
        <Button
          icon="angle-right"
          iconPosition="right"
          onClick={identificationRequired ? handleIdentify : handleContinue}
          disabled={isPending}
          variant="pill"
          small
          className="w-full justify-center min-tablet:w-auto"
        >
          {identificationRequired
            ? t('frontPage:pendingOperations.identify')
            : t('frontPage:pendingOperations.continue')}
        </Button>
        <Button
          variant="cancel"
          onClick={handleReject}
          disabled={isPending || isImpersonator}
          small
          className="w-full justify-center min-tablet:w-auto"
        >
          {t('frontPage:pendingOperations.reject')}
        </Button>
      </div>
      {rejectFailed && (
        <Paragraph className="mt-2 text-common-red-6">
          {t('frontPage:pendingOperations.rejectError')}
        </Paragraph>
      )}
    </ContextNotification>
  )
}
